import { ng } from '@uirouter/angularjs/lib/angular';
import { StateParams } from '@uirouter/core';
import * as angular from 'angular';
import * as moment from 'moment';
import { ngStorage } from 'ngstorage';
import { taOptions } from 'textangular';
import * as _ from 'underscore';
import { WysiwygControllerBase } from '../../../app.common/modules/common/controllers/wysiwygControllerBase';
import { PopupServiceCancel } from '../../../app.common/modules/common/models/popupServiceCancel';
import { DefinitionsService } from '../../../app.common/modules/common/services/definitions.service';
import { HtmlService } from '../../../app.common/modules/common/services/html.service';
import { NotificationService } from '../../../app.common/modules/common/services/notification.service';
import { PopupService } from '../../../app.common/modules/common/services/popup.service';
import { WysiwygService } from '../../../app.common/modules/common/services/wysiwyg.service';
import { PreviousStateService } from '../../../app.common/modules/routes/previous-state.service';
import { AccountService } from '../account/account.service';
import { IUser } from '../account/model';
import { BrandsService } from '../brands/brands.service';
import { CategoriesSelectorService } from '../categories/categoriesSelector.service';
import {
    CategoryListItem,
    EnumListItem,
    MenuPreference,
    PhysicalProductClassificationCategory,
    PhysicalProductMetadata,
    PhysicalProductVariation,
    PlaceListShortModelPagingResult,
    RetailerProfile
} from '../core';
import { IRetailerRootScope } from '../core/models';
import { FoodPreferenceService } from '../foodPreferences/foodPreference.service';
import { IProductIngredient } from '../model/products/IProductIngredient';
import { IProductVariationIngredient } from '../model/products/IProductVariationIngredient';
import { NfcRetailerStickersService } from '../nfc-stickers/nfc-retailer-stickers.service';
import {
    IBadge,
    IFoodPreference,
    ILinkListItem,
    IMeasurementListItem,
    INfcStickersErrors,
    IPhysicalProduct,
    IPhysicalProductLink,
    IPhysicalProductVariation,
    ISpecialOffer,
    ISpecialOfferChangeMessage
} from './models';
import { PaginatedItemTypeList } from './paginated-item-type-list';
import { ProductsService } from './products.service';
import { OffersService } from './services/offers.service';

interface IProductState {
    isEdit: boolean;
    isCopy: boolean;
    isNew: boolean;
    saveTitle: string;
}

export interface IStoresListItem {
    ID?: number;
    Name?: string;
}

export interface IDiscountChangeMessage {
    variationID: string;
    discount?: number;
}

export interface IPreparationChangeMessage {
    variationID: string;
    preparationTime: number;
    isHot: boolean;
}

export abstract class ProductBaseController extends WysiwygControllerBase {
    public waiting = false;
    public state: IProductState = null;
    public productForm: ng.IFormController;
    public variation: IPhysicalProductVariation = null;
    public currencySign: string;
    public currencyCssClass: string;
    public profile: RetailerProfile;
    public stores: IStoresListItem[];
    public canEditStickers: boolean;
    public allergenTitle: string;
    public preferenceTitle: string;
    public commonAllergenDescription: string;
    public commonPreferenceDescription: string;
    public allAllergens: MenuPreference[];
    public allPreference: MenuPreference[];
    public readonly foodCategoryId: number = 228; // Meals
    public readonly groceryId: number = 11; // Groceries
    public IsFoodProduct: boolean = false;
    public isGroceryProduct: boolean = false;
    public originCountryList: EnumListItem[];
    public countryID: number;
    public discountMessage: IDiscountChangeMessage;
    public isNfrnRetailer: boolean;
    public specialOfferMessage: ISpecialOfferChangeMessage;
    public saveDisabled: boolean;
    public isPublisher: boolean;
    public paginatedItemTypes: PaginatedItemTypeList;

    public model = {
        main: null as IPhysicalProduct,
        edit: null as IPhysicalProduct,
        editSample: null,
        linkTypes: [] as ILinkListItem[],
        serverImagesID: [] as string[],
        serverVariationImagesID: [] as number[][],
        draft: {
            fields: {
                category: {
                    state: false,
                    name: this.$translate.instant('category')
                },
                images: {
                    state: false,
                    name: this.$translate.instant('product_variation_image')
                },
                prices: {
                    state: false,
                    name: this.$translate.instant('product_variation_price')
                },
                document: {
                    state: false,
                    name: this.$translate.instant('digital_content_file')
                }
            },
            setAll: (state: boolean) => {
                const i = this.model.draft;
                _.forEach(i.fields, (field) => {
                    field.state = state;
                });
            },
            verify: (product: IPhysicalProduct) => {
                const i = this.model.draft;
                i.fields.category.state = product.category == null;

                i.fields.images.state =
                    _.size(product.variations) === 0 ||
                    _.any(product.variations, (variation) => {
                        return _.size(variation.imagesID) === 0;
                    });
                if (!this.isPublisher) {
                    i.fields.prices.state = _.any(
                      product.variations,
                      (variation) => {
                          return _.isNaN(parseFloat(variation.price as string));
                      }
                    );
                }

                if (this.isPublisher) {
                    i.fields.document.state = _.any(
                      product.variations,
                      (variation) => {
                          return !!!variation.attachment;
                      });
                }
            },
            getDraftFields: (product: IPhysicalProduct) => {
                const i = this.model.draft;
                i.verify(product);
                return _.filter(i.fields, (field) => {
                    return field.state;
                });
            }
        },
        fillServerImagesID: () => {
            const i = this.model;
            i.serverVariationImagesID = [];
            i.serverImagesID = [];
            if (this.state.isEdit && i.main && i.main.variations) {
                let imagesID = i.serverImagesID;
                const variationImagesID = i.serverVariationImagesID;
                _.forEach(i.main.variations, (variation) => {
                    const array = variation.imagesID ? variation.imagesID : [];
                    variationImagesID[variation.id] = array;
                    imagesID = Array.prototype.concat(imagesID, array);
                });
                i.serverImagesID = imagesID;
            }
        },
        getServerVariationImagesID: (id: string) => {
            const i = this.model;
            if (!id && i.main && i.main.variations) {
                i.serverImagesID = _.flatten(
                    _.pluck(i.main.variations, 'imagesID')
                );
                return i.serverImagesID;
            }
            return [] as string[];
        },
        getClientImagesID: () => {
            const i = this.model;
            if (i.edit && i.edit.variations) {
                return _.flatten(
                    _.pluck(i.edit.variations, 'imagesID')
                ) as string[];
            }
            return [] as string[];
        },
        fillEditModel: () => {
            const model = this.model;
            model.edit = angular.copy(model.main);
            if (model.edit.category) {
                this.productTypeCheck(model.edit.category.ParentID);
            }
            model.fillServerImagesID();
            _.forEach(model.edit.links, (link) => {
                link.type = _.findWhere(model.linkTypes, { ID: link.Type });
            });
            model.edit.genderItem = _.findWhere(model.edit.genderItems, {
                ID: model.edit.gender
            });
            model.editSample = angular.copy(model.edit);
        },
        isValidModel: async (
            skipServerSideValidation: boolean
        ): Promise<boolean> => {
            const i = this.model;
            const model = i.edit;
            let valid = await i.isValidProduct(skipServerSideValidation);
            if (valid) {
                _.forEach(model.variations, (variation) => {
                    if (!i.isValidVariation(variation)) {
                        valid = false;
                    }
                });
            }
            if (valid) {
                valid = i.isValidArrayVariations();
            }
            if (valid) {
                model.error.summary = null;
            } else {
                model.error.summary = this.$translate.instant('form_has_error');
            }
            return valid;
        },
        isValidProduct: async (
            skipServerSideValidation: boolean
        ): Promise<boolean> => {
            const model = this.model.edit;
            // title
            model.title = model.title ? model.title.trim() : '';
            if (model.title.length === 0) {
                model.error.title = this.requiredfield;
            } else if (model.title.length > 64) {
                model.error.title = this.$translate.instant('title_too_long');
            } else {
                model.error.title = null;
            }
            model.error.category = null;
            model.error.brand = null;
            model.error.itemType = null;
            model.error.preparation = null;
            model.error.ingredients = null;
            model.error.originCountry = null;
            // description
            model.description = model.description
                ? model.description.trim()
                : '';
            if (model.description.length > 30000) {
                model.error.description = this.$translate.instant(
                    'description_too_long',
                    { maxLength: 30000 }
                );
            } else {
                model.error.description = null;
            }
            // shortDescription
            model.shortDescription = model.shortDescription
                ? model.shortDescription.trim()
                : '';
            if (model.shortDescription.length > 48) {
                model.error.shortDescription = this.$translate.instant(
                    'short_description_too_long',
                    { maxLength: 48 }
                );
            } else {
                model.error.shortDescription = null;
            }

            const isStickerAreValid = await this.model.isValidNfcStickers(
                skipServerSideValidation
            );
            const isExtrasValid = this.model.isValidExtras();

            return (
                model.error.title === null &&
                model.error.category == null &&
                model.error.shortDescription == null &&
                model.error.brand == null &&
                model.error.description == null &&
                this.model.isValidLinks() &&
                isStickerAreValid &&
                isExtrasValid &&
                this.model.isValidFoodProduct() &&
                this.model.isValidGrocery() &&
                this.model.isValidDigitalContent()
            );
        },
        isValidFoodProduct: () => {
            if (!this.IsFoodProduct) {
                return true;
            }
            const model = this.model.edit;
            if (
                model.itemType === undefined ||
                model.itemType === null ||
                model.itemType.ID === undefined
            ) {
                model.error.itemType = this.$translate.instant(
                    'itemType_required'
                );
            } else {
                model.error.itemType = null;
            }
            // preparation
            model.preparation = model.preparation
                ? model.preparation.trim()
                : '';
            if (model.preparation.length > 250) {
                model.error.preparation = this.$translate.instant(
                    'preparation_too_long',
                    { maxLength: 250 }
                );
            } else {
                model.error.preparation = null;
            }
            // ingredients
            model.ingredients = model.ingredients
                ? model.ingredients.trim()
                : '';
            if (model.ingredients.length > 2000) {
                model.error.ingredients = this.$translate.instant(
                    'ingredients_too_long',
                    { maxLength: 2000 }
                );
            } else {
                model.error.ingredients = null;
            }

            // ingredient list
            if (model.ingredientList.length > 0) {
                const badIngredient = model.ingredientList.find((ingredient) => ingredient.MinQuantity == null);
                if (badIngredient) {
                    model.error.ingredientList = this.$translate.instant('ingredients_list_required_error');
                }
            } else {
                model.error.ingredientList = null;
            }

            // country of origin
            if (model.originCountry === null) {
                model.error.originCountry = this.$translate.instant(
                    'originCountry_required_for_food'
                );
            } else {
                model.error.originCountry = null;
            }
            return (
                model.error.itemType === null &&
                model.error.preparation === null &&
                model.error.ingredients == null &&
                model.error.ingredientList == null &&
                model.error.originCountry == null
            );
        },
        isValidDigitalContent: () => {
            if (!this.isPublisher) {
                return true;
            }
            const model = this.model.edit;
            if (
                model.itemType === undefined ||
                model.itemType === null ||
                model.itemType.ID === undefined
            ) {
                model.error.itemType = this.$translate.instant(
                    'itemType_required'
                );
            } else {
                model.error.itemType = null;
            }

            return model.error.itemType === null;
        },
        isValidGrocery: () => {
            if (!this.isGroceryProduct) {
                return true;
            }

            const model = this.model.edit;

            model.ingredients = model.ingredients
                ? model.ingredients.trim()
                : '';
            if (model.ingredients.length > 2000) {
                model.error.ingredients = this.$translate.instant(
                    'ingredients_too_long',
                    { maxLength: 2000 }
                );
            } else {
                model.error.ingredients = null;
            }

            return model.error.ingredients === null;
        },
        isValidVariation: (variation: IPhysicalProductVariation) => {
            const model = this.model.edit;
            variation.error = {};
            variation.error.sizes = {};
            // sku
            variation.sku = variation.sku ? variation.sku.trim() : '';
            if (variation.sku.length > 64) {
                variation.error.sku = this.$translate.instant('sku_too_long');
            } else {
                variation.error.sku = null;
            }
            // color
            variation.color = variation.color ? variation.color.trim() : '';
            if (variation.color.length > 40) {
                variation.error.color = this.$translate.instant(
                    'color_too_long'
                );
            } else {
                variation.error.color = null;
            }
            // sizes
            let sizesError = false;

            if (model.measurementEnable) {
                _.forEach(model.measurementTypes, (type) => {
                    if (
                        model.measurementTypeEnable[type.ID] &&
                        variation.measurementTypeItem[type.ID] &&
                        variation.measurementTypeItem[type.ID].Value
                    ) {
                        const value = (variation.measurementTypeItem[
                            type.ID
                        ].Value = variation.measurementTypeItem[
                            type.ID
                        ].Value.trim());
                        if (value && value.length > 40) {
                            variation.error.sizes[
                                type.ID
                            ] = this.$translate.instant('size_too_long');
                            sizesError = true;
                        } else {
                            variation.error.sizes[type.ID] = null;
                        }
                    }
                });
            } else {
                variation.size = variation.size ? variation.size.trim() : '';
                if (variation.size.length > 40) {
                    variation.error.size = this.$translate.instant(
                        'size_too_long'
                    );
                    sizesError = true;
                } else {
                    variation.error.size = null;
                }
            }
            // price
            if (
              (variation.price === '' ||
                (!_.isNaN(parseFloat(variation.price as string)) &&
                    variation.price <= 0)) && !this.isPublisher
            ) {
                variation.error.price = this.$translate.instant(
                    'must_be_greater_than_0'
                );
            } else {
                variation.error.price = null;
            }
            // old price
            if (
                !_.isNaN(parseFloat(variation.oldPrice as string)) &&
                parseFloat(variation.oldPrice as string) <=
                    parseFloat(variation.price as string)
            ) {
                variation.error.oldPrice = this.$translate.instant( this.IsFoodProduct ?
                    'must_be_greater_than_takeaway_price' :
                    'must_be_greater_than_current_price');
            } else {
                variation.error.oldPrice = null;
            }
            // takeaway price greater eatIn
            if (
                !_.isNaN(parseFloat(variation.price as string)) &&
                (parseFloat(variation.insidePrice as string) !==
                parseFloat(variation.price as string)) &&
                (parseFloat(variation.insidePrice as string) <
                    parseFloat(variation.price as string))
            ) {
                variation.error.takeawayGreatEatIn = true;
            } else {
                variation.error.takeawayGreatEatIn = false;
            }

             // preparation time error
            if (variation.preparationTime > 1440) {
                variation.error.preparationTime = true;
            } else {
                variation.error.preparationTime = false;
            }

            if (variation.ingredients.length > 0) {

                const badIngredientWithEmptyFields = variation.ingredients.find((ingredient) => {

                    const badTakeawayPrice = () => {
                        if (variation.price === '' || variation.price == null) {
                            return false;
                        }
                        return ingredient.Price == null || Number.isNaN(ingredient.Price);
                    };

                    const badEatInPrice = () => {
                        if (variation.insidePrice === '' || variation.insidePrice == null) {
                            return false;
                        }
                        return ingredient.InsidePrice == null || Number.isNaN(ingredient.InsidePrice);
                    };

                    const badQuantity = () => ingredient.DefaultQuantity == null || ingredient.MaxQuantity == null;

                    return badTakeawayPrice() || badEatInPrice() || badQuantity();
                });

                if (badIngredientWithEmptyFields) {
                    variation.error.ingredient = true;
                } else {
                    variation.error.ingredient = model.ingredientList.some((ingredientItem) => {
                        const ingredient = variation.ingredients.find((variationIngredient) => {
                            return variationIngredient.ID === ingredientItem.ID;
                        });
                        const badQuantityValidation = ingredientItem.MinQuantity > ingredient.DefaultQuantity || ingredient.DefaultQuantity > ingredient.MaxQuantity;
                        let badPriceValidation = false;
                        if (variation.price && variation.insidePrice) {
                            if (ingredient.InsidePrice < ingredient.Price) {
                                badPriceValidation = true;
                            }
                        }
                        return badQuantityValidation || badPriceValidation;
                    });
                }
            } else {
                variation.error.ingredient = false;
            }

            // special offer
            const specialOfferError = variation.specialOffer && !this.offersService.isValidOffer(variation.specialOffer, variation.price);

            return (
                !variation.error.storeVariations &&
                !variation.error.image &&
                !variation.error.sku &&
                !variation.error.color &&
                !sizesError &&
                !variation.error.price &&
                !variation.error.oldPrice &&
                !variation.error.ingredient &&
                !variation.error.takeawayGreatEatIn &&
                !variation.error.preparationTime &&
                !specialOfferError
            );
        },
        isValidArrayVariations: () => {
            const model = this.model.edit;
            // variationsCount
            model.error.variations = null;
            const variationsCount = model.variations.length;
            if (variationsCount === 1) {
                return true;
            } else if (variationsCount === 0) {
                model.error.variations = this.$translate.instant(
                    'at_least_one_variation_required'
                );
                return false;
            }

            const countOfNullable = { sizes: {}, size: 0, color: 0 };
            _.forEach(model.variations, (variation) => {
                if (model.measurementEnable) {
                    _.forEach(model.measurementTypes, (type) => {
                        if (!countOfNullable.sizes[type.ID]) {
                            countOfNullable.sizes[type.ID] = 0;
                        }

                        if (model.measurementTypeEnable[type.ID]) {
                            if (
                                !(
                                    variation.measurementTypeItem[type.ID] &&
                                    variation.measurementTypeItem[type.ID].Value
                                )
                            ) {
                                countOfNullable.sizes[type.ID]++;
                            }
                        }
                    });
                } else if (variation.size === '') {
                    countOfNullable.size++;
                }

                if (variation.color === '') {
                    countOfNullable.color++;
                }
            });

            let checkAllNullable = true;
            let checkNotAllNullable = false;
            if (model.measurementEnable) {
                _.forEach(model.measurementTypes, (type) => {
                    if (model.measurementTypeEnable[type.ID]) {
                        checkAllNullable =
                            checkAllNullable &&
                            countOfNullable.sizes[type.ID] === variationsCount;

                        checkNotAllNullable =
                            checkNotAllNullable ||
                            (countOfNullable.sizes[type.ID] !== 0 &&
                                countOfNullable.sizes[type.ID] !==
                                    variationsCount);
                    }
                });
            } else {
                checkAllNullable = countOfNullable.size === variationsCount;
                checkNotAllNullable =
                    countOfNullable.size !== 0 &&
                    countOfNullable.size !== variationsCount;
            }

            // size or colour
            if (
                checkAllNullable &&
                countOfNullable.color === variationsCount &&
                variationsCount > 1
            ) {
                model.error.variations = this.$translate.instant(
                    'size_or_color_required',
                    {
                        sizeName: model.sizeName.toLowerCase(),
                        colourName: model.colourName.toLowerCase()
                    }
                );
                return false;
            }
            // all size values (or none)
            if (checkNotAllNullable) {
                model.error.variations = this.$translate.instant(
                    'all_size_values_or_none_are_required',
                    { sizeName: model.sizeName.toLowerCase() }
                );
                return false;
            }
            // all colour values (or none)
            if (
                countOfNullable.color !== 0 &&
                countOfNullable.color !== variationsCount
            ) {
                model.error.variations = !this.IsFoodProduct
                    ? this.$translate.instant(
                          'none_or_all_colour_should_be_blank'
                      )
                    : this.$translate.instant(
                          'none_or_all_choose_should_be_blank'
                      );
                return false;
            }
            // size/colour pair
            const uniq = _.uniq(model.variations, (variation) => {
                let sizes = '';
                if (model.measurementEnable) {
                    _.forEach(model.measurementTypes, (type) => {
                        if (
                            model.measurementTypeEnable[type.ID] &&
                            variation.measurementTypeItem[type.ID] &&
                            variation.measurementTypeItem[type.ID].Value
                        ) {
                            sizes += variation.measurementTypeItem[
                                type.ID
                            ].Value.toLowerCase();
                        }
                    });
                } else {
                    sizes += variation.size ? variation.size.toLowerCase() : '';
                }
                return variation.color
                    ? variation.color.toLowerCase() + sizes
                    : sizes;
            });

            if (uniq.length !== variationsCount) {
                model.error.variations = this.$translate.instant(
                    'size_and_colour_pair_should_be_unique',
                    {
                        sizeName: model.sizeName,
                        colorName: model.colourName.toLowerCase()
                    }
                );
                return false;
            }
            // clear not use sizes
            _.forEach(model.variations, (variation) => {
                _.forEach(model.measurementTypes, (type) => {
                    if (!model.measurementTypeEnable[type.ID]) {
                        variation.measurementTypeItem[type.ID] = null;
                    }
                });

                if (model.measurementEnable) {
                    variation.size = null;
                }
            });
            return true;
        },
        isValidLink: (link: IPhysicalProductLink) => {
            link.error = { sum: null };
            link.Title = link.Title ? link.Title.trim() : '';
            if (link.Title.length === 0) {
                link.error.title = this.$translate.instant('title_is_required');
            } else if (link.Title.length > 64) {
                link.error.title = this.$translate.instant('title_too_long');
            } else {
                link.error.title = null;
            }

            const regexUrl = this.definitionsService.regexUrl;
            link.Url = link.Url ? link.Url.trim() : '';
            if (link.Url.length === 0) {
                link.error.url = this.$translate.instant(
                    'web_address_is_required'
                );
            } else if (!regexUrl.test(link.Url)) {
                link.error.url = this.$translate.instant(
                    'web_address_is_invalid'
                );
            } else if (link.Url.length > 256) {
                link.error.url = this.$translate.instant(
                    'web_address_too_long'
                );
            } else {
                link.error.url = null;
            }

            if (!link.type) {
                link.error.type = this.$translate.instant(
                    'link_type_url_title_are_required'
                );
            } else {
                link.error.type = null;
            }

            if (link.error.type) {
                link.error.sum = link.error.type;
            } else if (link.error.url) {
                link.error.sum = link.error.url;
            } else if (link.error.title) {
                link.error.sum = link.error.title;
            }
            return !link.error.sum;
        },
        isValidLinks: () => {
            const i = this.model;
            let isValid = true;
            if (_.all(i.edit.links, i.isValidLink)) {
                // url uniq
                _.forEach(i.edit.links, (current) => {
                    if (isValid) {
                        const eqUrl = _.where(i.edit.links, {
                            Url: current.Url
                        });
                        if (_.size(eqUrl) > 1) {
                            isValid = false;
                            _.forEach(eqUrl, (link) => {
                                link.error.sum = this.$translate.instant(
                                    'web_address_must_be_unique'
                                );
                                link.error.url = this.$translate.instant(
                                    'web_address_must_be_unique'
                                );
                            });
                        }
                    }
                });
                return isValid;
            }
            return false;
        },
        isValidNfcStickers: (
            skipServerSideValidation: boolean
        ): Promise<boolean> => {
            this.model.edit.error.stickers = [];
            const editModel = this.model.edit;
            if (
                !editModel.nfcStickers ||
                (editModel.nfcStickers && editModel.nfcStickers.length === 0)
            ) {
                return this.$q.when(true);
            }

            const STICKER_NUMBER_PATTERN = new RegExp('[A-Z]{1}\\d{12}$');

            let stickersError = false as boolean;
            const duplicatesMap = _.groupBy(
                editModel.nfcStickers,
                (item) => item.StickerNumber
            );

            editModel.nfcStickers.forEach((sticker, index) => {
                let stickerHasError = false as boolean;
                const stickerError = {
                    id: index,
                    nfcNumber: null,
                    store: null
                } as INfcStickersErrors;

                if (!sticker.StickerNumber) {
                    stickerHasError = true;
                    stickerError.nfcNumber = this.$translate.instant(
                        'nfc_product_stickers_list_sticker_required_error'
                    );
                }
                if (sticker.StickerNumber) {
                    if (!STICKER_NUMBER_PATTERN.test(sticker.StickerNumber)) {
                        stickerHasError = true;
                        stickerError.nfcNumber = this.$translate.instant(
                            'nfc_product_stickers_list_sticker_format_error'
                        );
                    } else {
                        const duplicates = duplicatesMap[sticker.StickerNumber];
                        if (duplicates && duplicates.length > 1) {
                            stickerHasError = true;
                            stickerError.nfcNumber = this.$translate.instant(
                                'sticker_is_already_used'
                            );
                        }
                    }
                }
                if (!sticker.PlaceID) {
                    stickerHasError = true;
                    stickerError.store = this.$translate.instant(
                        'nfc_product_stickers_list_location_required_error'
                    );
                }
                if (stickerHasError) {
                    this.model.edit.error.stickers.push(stickerError);
                    stickersError = true;
                }
            });

            if (stickersError) {
                return this.$q.when(false);
            }

            if (skipServerSideValidation) {
                return this.$q.when(true);
            }

            this.waiting = true;
            this.notificationService.showWaitingMessage();
            return this.nfcRetailerStickersService
                .checkStickersAvailable(
                    _.map(editModel.nfcStickers, (s) => s.StickerNumber),
                    this.state.isEdit ? this.productID : null
                )
                .then(() => {
                    this.waiting = false;
                    this.notificationService.clearAll();
                    return true;
                })
                .catch((error) => {
                    this.waiting = false;
                    this.notificationService.showErrorMessage(error);
                    this.$timeout(
                        () => this.notificationService.clearAll(),
                        1000
                    );
                    return false;
                });
        },
        isValidExtras: () => {
            const editModel = this.model.edit;
            if (!editModel.extras) {
                return true;
            }
            let isValid = true as boolean;

            editModel.extras.forEach((extra) => {
                extra.QuantityError = null;

                if (!extra.MaxAmount || extra.MaxAmount < 0) {
                    extra.QuantityError = this.$translate.instant(
                        'extra_list_column_max_quantity_error'
                    );
                    isValid = false;
                }
            });

            return isValid;
        },
        isValidIngredients: () => {
            return true;
        },
        loadedModel: () => {
            return;
        }
    };
    protected attachmentsForRemove: string[] = [];

    private requiredfield = this.$translate.instant('required_field');
    private productID: string = null;
    private productMetadata: PhysicalProductMetadata;

    constructor(
        protected $rootScope: IRetailerRootScope,
        protected $scope: ng.IScope,
        protected $q: ng.IQService,
        protected $stateParams: StateParams,
        protected $timeout: ng.ITimeoutService,
        protected $previousState: PreviousStateService,
        protected productsService: ProductsService,
        protected categoriesSelectorService: CategoriesSelectorService,
        protected brandsService: BrandsService,
        protected popupService: PopupService,
        // tslint:disable-next-line:no-shadowed-variable
        protected taOptions: taOptions,
        protected htmlService: HtmlService,
        protected definitionsService: DefinitionsService,
        protected wysiwygService: WysiwygService,
        protected notificationService: NotificationService,
        protected accountService: AccountService,
        protected $translate: angular.translate.ITranslateService,
        protected nfcRetailerStickersService: NfcRetailerStickersService,
        protected foodPreferencesService: FoodPreferenceService,
        protected $localStorage: ngStorage.StorageService,
        protected offersService: OffersService,
        protected uibModal: ng.ui.bootstrap.IModalService
    ) {
        super($scope, taOptions, wysiwygService);
        this.productID = this.$stateParams.id;
        this.state = {
            isEdit:
                this.productID &&
                $rootScope.$state.current.name.startsWith('app.product.edit'),
            isCopy:
                this.productID &&
                $rootScope.$state.current.name.startsWith('app.product.copy'),
            isNew: $rootScope.$state.current.name.startsWith('app.product.new'),
            saveTitle:
                this.productID &&
                $rootScope.$state.current.name.startsWith('app.product.edit')
                    ? this.$translate.instant('save')
                    : this.$translate.instant('create')
        };

        const unwatch = $rootScope.$watch('userData', async (userData: IUser) => {
            if (userData.permission) {
                unwatch();
                this.isNfrnRetailer = userData.permission === this.$rootScope.enums.RetailerPermission.Nfrn;
                this.isPublisher = userData.permission === this.$rootScope.enums.RetailerPermission.Publisher;
            }
        }, true);
        this.init();
    }

    public clearEatInTakeawayError(variation: IPhysicalProductVariation) {
        variation.error.takeawayGreatEatIn = false;
    }

    public loadFoodPreferenceInfo = async () => {
        this.allergenTitle = this.$translate.instant('allergens_title');
        this.preferenceTitle = this.$translate.instant('foodPreference_title');
        this.allAllergens = await this.foodPreferencesService.getAllergens();
        this.allPreference = await this.foodPreferencesService.getFoodPreference();
        this.commonAllergenDescription = this.bindDescription(
            this.allAllergens
        );
        this.commonPreferenceDescription = this.bindDescription(
            this.allPreference
        );
    }

    public loadOriginCountry = async () => {
        this.originCountryList = await this.productsService.GetCountryOrigins();
    }

    public getTooltipText = (variation: IPhysicalProductVariation) => {
        return variation.id
            ? this.$translate.instant('archive')
            : this.$translate.instant('delete');
    }

    public discard = () => {
        this.deleteImages();
        this.back();
    }

    public getImageUrl = (imageIds: string[] | string) => {
        if (!imageIds) {
            return null;
        }
        if (_.isArray(imageIds)) {
            return this.productsService.getImageUrl(_.first(imageIds));
        } else {
            return this.productsService.getImageUrl(imageIds);
        }
    }

    public deleteImage = async (id: string) => {
        const imageID = id;
        try {
            if (imageID || _.contains(this.model.serverImagesID, imageID)) {
                // return;
            }
            const ok = await this.productsService.deleteImage(imageID);
            if (ok) {
                console.log('Deleted imageID - ' + imageID);
            } else {
                console.log('Could not remove image with id - ' + imageID);
            }
        } catch (error) {
            console.error(
                error + ' Image with id ' + imageID + ' is not removed'
            );
        }
    }

    public removeVariation = async (
        variation: IPhysicalProductVariation,
        callback: () => void
    ) => {
        if (_.size(this.model.edit.variations) === 1) {
            return;
        }
        const isDelete = variation.id === null;
        try {
            const popupTitle = this.$translate.instant(
                isDelete ? 'delete_variation' : 'archive_variation'
            );
            const popupContent = `<p>${this.$translate.instant(
                isDelete
                    ? 'delete_variation_confirm'
                    : 'archive_variation_confirm'
            )}</p>`;

            await this.popupService.confirm({
                title: popupTitle,
                content: popupContent
            });
            this.$removeVariation(variation);
            if (_.isFunction(callback)) {
                callback();
            }
        } catch (error) {
            if (!PopupServiceCancel.isCancelOrBackdropClickInstance(error)) {
                this.notificationService.showErrorMessage(error);
            }
        }
    }

    public addVariation = () => {
        const defaultVariation = this.productsService.getDefaultVariationModel();
        const ingredientList = this.model.edit.ingredientList;
        if (ingredientList.length > 0) {
            ingredientList.forEach((item) => {
                const ingredient: IProductVariationIngredient = {
                    ID: item.ID,
                    Title: item.Title,
                    Price: null,
                    InsidePrice: null,
                    DefaultQuantity: item.MinQuantity,
                    MaxQuantity: 0
                };
                defaultVariation.ingredients.push(ingredient);
            });
        }
        this.model.edit.variations.push(defaultVariation);
        if (this.productForm) {
            this.productForm.$setDirty();
        }
    }

    public back = () => {
        const pSt = this.$previousState.get();
        const stateNameProducts = 'app.products';
        if (
            this.state.isEdit &&
            pSt &&
            pSt.state.name === stateNameProducts &&
            pSt.params &&
            pSt.params.page
        ) {
            this.$rootScope.$state.go(stateNameProducts, {
                page: pSt.params.page,
                search: pSt.params.search,
                column: pSt.params.column,
                order: pSt.params.order
            });
        } else {
            this.$rootScope.$state.go(stateNameProducts);
        }
    }

    public save = async () => {
        this.waiting = true;
        const isModelValid = await this.model.isValidModel(true);
        if (!isModelValid) {
            window.scroll(0, 0);
            this.waiting = false;
            return;
        }
        const draftFields = this.model.draft.getDraftFields(this.model.edit);
        try {
            let result;
            if (_.size(draftFields) > 0) {
                result = await this.uibModal.open({
                    component: 'draftConfirmationDialog',
                    resolve: {
                        items: () => draftFields.map((element) => {
                            return element.name;
                        })
                    },
                    windowClass: 'dpp-info-dialog'
                }).result;
            }
            if (_.size(draftFields) === 0 || result) {
                if (this.isPublisher) {
                    this.model.edit.variations[0].price = 0;
                }
                await this.$save();
            }
        } catch (error) {
            this.notificationService.showErrorMessage(error);
        }
        this.waiting = false;
    }

    public selectCategory = async () => {
        try {
            const modelSelectCategories: CategoryListItem[] = [];
            if (this.model.edit.category) {
                modelSelectCategories.push({
                    CategoryID: this.model.edit.category.ID,
                    CategoryFullName: this.model.edit.category.FullName,
                    ParentID: this.model.edit.category.ParentID
                    // Brands: this.model.edit.brands ? this.model.edit.brands : []
                });
            }

            const businessTypeEnum = this.$rootScope.enums.BusinessType;
            let businessType = this.isNfrnRetailer ? businessTypeEnum.Shop : businessTypeEnum.All;
            businessType = this.isPublisher ? businessTypeEnum.DigitalContentPublisher : businessType;
            const subcategoryId = this.isNfrnRetailer ? this.groceryId : null;
            const categories = await this.categoriesSelectorService.selectClassificationCategories(
                modelSelectCategories,
                businessType,
                true,
                null,
                subcategoryId
            );

            if (categories) {
                this.productTypeCheck(categories[0].ParentID);
                const productCategories = _.map(categories, (item) => {
                    return {
                        ID: item.CategoryID,
                        Name: _.last(item.CategoryFullName.split('>')).trim(),
                        FullName: item.CategoryFullName
                    };
                });
                const selectCategory = _.first(
                    productCategories
                ) as PhysicalProductClassificationCategory;
                if (
                    !this.model.edit.category ||
                    this.model.edit.category.ID !== selectCategory.ID
                ) {
                    this.model.edit.category = selectCategory;
                    this.productForm.$setDirty();
                    this.model.edit.brands = [];
                    this.clearSizeValues();
                }
            }
        } catch (error) {
            if (!PopupServiceCancel.isCancelOrBackdropClick(error)) {
                this.notificationService.showErrorMessage(error);
            }
        }
    }

    public badgeClick = (badge: IBadge, badges: IBadge[]) => {
        if (badge.key === 0) {
            badge.selected = true;
            _.forEach(_.rest(badges), (item) => {
                item.selected = false;
            });
        } else {
            badges[0].selected = !_.any(_.rest(badges), (item) => {
                return item.selected;
            });
        }
    }

    public isBadgeDisabled = (badge: IBadge, badgeList: IBadge[], specialOffer: ISpecialOffer): boolean => {
        if (badge.key === 128) {
            if (specialOffer) {
                const utcToday: Date = moment(new Date()).utc(true).startOf('day').toDate();
                const isEndDateExpired = moment.utc(utcToday).isAfter(moment.utc(specialOffer.OfferValidTo), 'date');
                if (isEndDateExpired) {
                    badge.selected = false;
                    return true;
                }
            } else {
                badge.selected = false;
                return true;
            }
        }
        const selectedBadges = badgeList.filter((i) => i.selected);
        return selectedBadges.length > 1 && !badge.selected;
    }

    public updateSelectedSizes = (variation: IPhysicalProductVariation) => {
        const measurementTypeIDs: number[] = _.pluck(
            this.model.edit.measurementTypes,
            'ID'
        );
        _.forEach(measurementTypeIDs, (id) => {
            let selectedItem = _.findWhere(
                this.model.edit.measurementItems[id],
                { Value: variation.measurementItemValues[id] }
            );

            if (!selectedItem && variation.measurementItemValues[id]) {
                selectedItem = {
                    ID: null,
                    Value: variation.measurementItemValues[id]
                };
                if (this.model.edit.measurementItems[id]) {
                    this.model.edit.measurementItems[id].push(selectedItem);
                } else {
                    this.model.edit.measurementItems[id] = [selectedItem];
                }
            }
            variation.measurementTypeItem[id] = selectedItem;
        });
    }

    public addLink = () => {
        if (!this.model.isValidLinks()) {
            return;
        }
        const link = {
            type: null,
            Title: '',
            Url: '',
            error: null
        };
        this.model.edit.links.push(link);
        this.productForm.$setDirty();
    }

    public removeLink = (index: number) => {
        this.model.edit.links.splice(index, 1);
        this.productForm.$setDirty();
    }

    public pasteHandler = (html: string) => {
        return this.htmlService.filterWysiwygContent(html);
    }

    public refreshMeasurementTypeItem = ($select: any) => {
        const search = $select.search;
        let list = angular.copy($select.items);

        // remove last user input
        list = list.filter((item) => {
            return item.ID;
        });

        if (
            search &&
            list.findIndex(
                (x: any) => x.Value.toLowerCase() === search.toLowerCase()
            ) === -1
        ) {
            // manually add user input and set selection
            const userInputItem = {
                ID: null,
                Value: search,
                forAdd: true
            };
            $select.items = list.concat([userInputItem]);
        } else {
            // se the predefined list
            $select.items = list;
        }
    }

    public selectMeasurementItem = (
        item: IMeasurementListItem,
        measurementType: number,
        variation: IPhysicalProductVariation
    ) => {
        if (!variation.measurementTypeItem) {
            variation.measurementTypeItem = {};
        }
        variation.measurementTypeItem[measurementType] = item;

        this.addNewMeasurementItem(item, measurementType, variation);
    }

    public addNewMeasurementItem = (
        item: IMeasurementListItem,
        measurementType: number,
        variation: IPhysicalProductVariation
    ) => {
        if (item && measurementType && variation) {
            variation.measurementItemValues[measurementType] = item.Value;
            this.updateMeasurementTypesEnable();
        }
    }

    public clearMeasurementItem = (
        $event: Event,
        $select: any,
        measurementType: number,
        variation: IPhysicalProductVariation
    ) => {
        $event.stopPropagation();
        // to allow empty field, in order to force a selection remove the following line
        $select.selected = null;
        // reset search query
        $select.search = '';

        variation.measurementTypeItem[measurementType] = null;
        variation.measurementItemValues[measurementType] = null;
        this.updateMeasurementTypesEnable();
    }

    public clearSizeValues() {
        _.forEach(this.model.edit.variations, (variation) => {
            variation.size = null;
            variation.measurementItemValues = {};
        });
    }

    public getCurrencyCssClass(sign: string) {
        switch (sign) {
            case '£':
                return 'withPound';
            case 'kr':
                return 'withKrona';
            case '$':
                return 'withDollar';
            case '€':
                return 'withEuro';
            case 'د.إ':
                return 'withDirham';
            default:
                return '';
        }
    }

    public onAddNewIngredient(item: IProductIngredient) {
        const variations = this.model.edit.variations;
        variations.forEach((variation) => {
            const variationIngredient: IProductVariationIngredient = {
                ID: item.ID,
                Title: item.Title,
                Price: null,
                InsidePrice: null,
                DefaultQuantity: item.MinQuantity,
                MaxQuantity: 1
            };
            variation.ingredients.push(variationIngredient);
            variation.ingredients.sort((a, b) => a.Title.localeCompare(b.Title));
        });
    }

    public onDeleteIngredient(item: IProductIngredient) {
        const variations = this.model.edit.variations;
        variations.forEach((variation) => {
           if (variation.ingredients.length > 0) {
              const foundIngredientIndex = variation.ingredients.findIndex((ingredient) => ingredient.ID === item.ID);
              if (foundIngredientIndex > -1) {
                  variation.ingredients.splice(foundIngredientIndex, 1);
                  variation.ingredients.sort((a, b) => a.Title.localeCompare(b.Title));
              }
           }
        });
    }

    public onDeleteIngredients(items: IProductIngredient[]) {
        if (items.length > 0) {
            items.forEach((item) => this.onDeleteIngredient(item));
        }
    }

    public async showIngredientAttentionDialog() {
        try {
            await this.popupService.alert({
                classPopup: 'alert--redesign modal360',
                title: this.$translate.instant('ingredient_attention_dialog_title'),
                content: this.$translate.instant('ingredient_attention_dialog_content'),
                okTxt: this.$translate.instant('ingredient_attention_dialog_ok_button'),
                okClass: 'btn btn-success',
            });
        } catch (e) { return; }
    }

    public async openStockLevelInfo() {
        try {
            await this.popupService.info({
                classPopup: 'alert--redesign modal520',
                title: this.$translate.instant('stock_level_label'),
                content: this.$translate.instant('stock_level_info_dialog_content')
            });
        } catch (e) { return; }
    }

    public onChangeSpecialOffer(message?: ISpecialOfferChangeMessage) {
        if (this.productForm) {
            this.productForm.$setDirty();
        }
    }

    protected updateVariationPreview = () => {
        return;
    }

    private bindDescription(preference: MenuPreference[]): string {
        let result = '';
        result += '<table class="preferenceDescription">';
        let counter = 0;
        preference
            .sort((a, b) =>
                a.Description.length > b.Description.length
                    ? -1
                    : b.Description.length > a.Description.length
                    ? 1
                    : 0
            )
            .forEach((c) => {
                if (counter % 3 === 0) {
                    result += '<tr>';
                }

                result +=
                    '<td><h3>' +
                    c.Name +
                    '</h3>' +
                    c.Description +
                    '</br></td>';
                counter++;

                if (counter % 3 === 0) {
                    result += '</tr>';
                }
            });
        result += '</table>';
        return result;
    }

    private getOppositeMeasurementType = (measurementType: number) => {
        switch (measurementType) {
            case this.$rootScope.enums.MeasurementType.Chest:
                return this.$rootScope.enums.MeasurementType.Bust;
            case this.$rootScope.enums.MeasurementType.Bust:
                return this.$rootScope.enums.MeasurementType.Chest;
            case this.$rootScope.enums.MeasurementType.InsideLeg:
                return this.$rootScope.enums.MeasurementType.LegLength;
            case this.$rootScope.enums.MeasurementType.LegLength:
                return this.$rootScope.enums.MeasurementType.InsideLeg;
            default:
                return null;
        }
    }

    private updateMeasurementTypesEnable = () => {
        const gender = this.model.edit.genderItem
            ? this.model.edit.genderItem.ID
            : null;
        if (gender === null) {
            _.forEach(this.model.edit.measurements, (measurement) => {
                const oppositeType = this.getOppositeMeasurementType(
                    measurement.Type
                );
                if (oppositeType) {
                    const allVariations = angular.copy(
                        this.model.edit.variations
                    );

                    if (this.variation) {
                        allVariations.push(this.variation);
                    }

                    const existValue = _.any(allVariations, (variation) => {
                        return !!variation.measurementItemValues[
                            measurement.Type
                        ];
                    });
                    this.model.edit.measurementTypeEnable[
                        oppositeType
                    ] = existValue
                        ? false
                        : this.model.edit.measurementTypeVisible[oppositeType];
                }
            });
        }
        _.forEach(this.model.edit.variations, (variation) => {
            this.updateSelectedSizes(variation);
        });
    }

    private getMeasurements = async (gender: number) => {
        this.waiting = true;
        this.notificationService.showWaitingMessage();
        try {
            const response = await this.$q.all([
                this.productsService.getMeasurements(gender),
                this.productsService.getSmlMeasurements(gender)
            ]);
            this.model.edit.measurements = response[0];
            const smlMeasurements = response[1];
            _.forEach(this.model.edit.measurements, (measurement) => {
                this.model.edit.measurementItems[measurement.Type] =
                    measurement.Sizes;

                if (smlMeasurements) {
                    const sml = _.findWhere(smlMeasurements, {
                        Type: measurement.Type
                    });
                    if (sml) {
                        _.forEach(sml.Sizes, (smlSize) => {
                            this.model.edit.measurementItems[
                                measurement.Type
                            ].push({
                                ID: smlSize.Value,
                                Value: smlSize.Value
                            });
                        });
                    }
                }
            });

            _.forEach(this.model.edit.measurementTypes, (measurementType) => {
                if (!this.model.edit.measurementItems[measurementType.ID]) {
                    this.model.edit.measurementItems[measurementType.ID] = [];
                }
            });

            _.forEach(this.model.edit.variations, (variation) => {
                this.updateSelectedSizes(variation);
            });
            this.notificationService.clearAll();
        } catch (error) {
            this.notificationService.showErrorMessage(error);
        }
        this.waiting = false;
    }

    private deleteImages = () => {
        _.forEach(this.model.getClientImagesID(), (id) => {
            this.deleteImage(id);
        });
        this.productForm.$setDirty();
    }

    private $removeVariation = (variation: IPhysicalProductVariation) => {
        this.model.edit.variations = _.difference(this.model.edit.variations, [
            variation
        ]);
        _.forEach(variation.imagesID, this.deleteImage);
        this.productForm.$setDirty();
    }

    private sortImageID = (variation: PhysicalProductVariation) => {
        variation.ImagesID.sort();
    }

    private isNeedSplitProduct = () => {
        return false; // TODO: implement later with access rights feature
    }

    private saveInternal = async () => {
        // this.waiting = true;
        this.notificationService.showWaitingMessage();
        try {
            const model = angular.copy(this.model.edit);
            model.variations.forEach((variation) => {
                if (!variation.sku) {
                    variation.sku = null;
                }
                if (!variation.insidePrice) {
                    variation.insideVAT = null;
                    if (variation.ingredients.length > 0) {
                        variation.ingredients.forEach((ingredient) => {
                            ingredient.InsidePrice = 0;
                        });
                    }
                }

                if (!variation.price) {
                    if (variation.ingredients.length > 0) {
                        variation.ingredients.forEach((ingredient) => {
                            ingredient.Price = 0;
                        });
                    }
                }

                if (!this.IsFoodProduct) {
                    variation.isHot = null;
                    variation.preparationTime = null;
                    model.originCountry = null;
                }

                if (this.IsFoodProduct && !variation.preparationTime) {
                    variation.preparationTime = 0;
                }

            });
            // model.tags = _.where(model.tags, { IsAllowedStore: true });
            if (!this.saveDisabled) {
                if (this.state.isEdit) {
                    await this.productsService.updateProduct(
                      this.productID,
                      model
                    );
                } else {
                    await this.productsService.createProduct(model);
                }
                const promieses = [];
                for (const id of this.attachmentsForRemove) {
                    promieses.push(this.productsService.deleteAttachment(id));
                }
                await this.$q.all(promieses);
            }
            this.saveDisabled = true;
            this.notificationService.showSuccessMessage();
            this.instanceWysiwygService.deleteRemovedTokens(model.description);
            this.$timeout(this.back, 1000);
        } catch (error) {
            this.notificationService.showErrorMessage(error);
        }
        // this.waiting = false;
    }

    private $save = async () => {
        try {
            if (this.isNeedSplitProduct()) {
                await this.productsService.showSaveChangesDialog();
            }
            await this.saveInternal();
        } catch (error) {
            if (!PopupServiceCancel.isCancelOrBackdropClick(error)) {
                this.notificationService.showErrorMessage(error);
            }
        } finally {
            this.notificationService.clearAll();
        }
    }

    private getItemTypes = async (categoryID: number, gender: number) => {
        this.notificationService.showWaitingMessage();
        try {
            const itemTypes = await this.productsService.getItemTypes(
                categoryID,
                gender
            );
            this.model.edit.itemTypes = _.sortBy(itemTypes, 'SingleForm');
            this.paginatedItemTypes = new PaginatedItemTypeList(this.model.edit.itemTypes);
            if (_.size(this.model.edit.itemTypes) === 1) {
                this.model.edit.itemType = _.first(this.model.edit.itemTypes);
            } else {
                this.model.edit.itemType = _.findWhere(
                    this.model.edit.itemTypes,
                    {
                        ID: this.model.edit.itemType
                            ? this.model.edit.itemType.ID
                            : this.model.edit.itemTypeID
                    }
                );
            }

            const doOnChange =
                this.model.edit.itemTypeID ===
                (this.model.edit.itemType ? this.model.edit.itemType.ID : null);

            this.model.edit.itemTypeID = this.model.edit.itemType
                ? this.model.edit.itemType.ID
                : null;
            this.model.editSample.itemType = this.model.editSample.itemType
                ? this.model.editSample.itemType
                : this.model.edit.itemType;

            if (doOnChange) {
                this.onChangeItemType();
            }
        } catch (error) {
            this.notificationService.showErrorMessage(error);
        } finally {
            this.notificationService.clearAll();
        }
    }

    private onChangeItemType = () => {
        if (!this.model.edit) {
            return;
        }

        _.forEach(this.model.edit.measurementTypes, (mType) => {
            this.model.edit.measurementTypeEnable[mType.ID] = false;
            this.model.edit.measurementTypeVisible[mType.ID] = false;
        });

        if (
            this.model.edit.itemType &&
            this.model.edit.itemType.MeasurementTypes &&
            this.model.edit.itemType.MeasurementTypes.length > 0
        ) {
            _.forEach(this.model.edit.itemType.MeasurementTypes, (mType) => {
                this.model.edit.measurementTypeEnable[mType] = true;
                this.model.edit.measurementTypeVisible[mType] = true;
            });
            this.model.edit.measurementEnable = true;
        } else {
            this.model.edit.measurementEnable = false;
        }

        this.updateMeasurementTypesEnable();
    }

    private init = async () => {
        this.waiting = true;
        this.notificationService.showWaitingMessage();
        await this.loadFoodPreferenceInfo();
        await this.loadOriginCountry();
        try {
            this.$scope.$watch(
                'vm.model.edit.category.ID',
                (n: number, o: number) => {
                    if (n && n !== o) {
                        this.getItemTypes(
                            n,
                            this.model.edit.genderItem
                                ? this.model.edit.genderItem.ID
                                : null
                        );
                        if (o) {
                            this.productForm.$setDirty();
                        }
                        this.productsService
                            .getCategoryFieldNames(n)
                            .then((fieldNames) => {
                                this.model.edit.sizeName = fieldNames.SizeName;
                                this.model.edit.colourName =
                                    fieldNames.ColourName;
                                if (!this.model.edit.colourName) {
                                    _.forEach(
                                        this.model.edit.variations,
                                        (variant) => {
                                            variant.color = '';
                                        }
                                    );
                                }
                            });
                    }
                }
            );
            const getProduct =
                this.state.isEdit || this.state.isCopy
                    ? this.productsService.getProductModel(
                          this.productID,
                          this.state.isEdit
                      )
                    : this.productsService.getDefaultProductModel();
            const getLocations = this.nfcRetailerStickersService.getLocations(
                {}
            );
            const checkCanEditStickers = this.nfcRetailerStickersService.checkCanEditStickers();
            const response = await this.$q.all([
                getProduct as angular.IPromise<IPhysicalProduct>,
                this.productsService.getLinkTypes() as angular.IPromise<ILinkListItem[]>,
                this.productsService.getGenders() as ILinkListItem[],
                this.productsService.getMeasurementTypes(),
                this.productsService.getMetadata() as PhysicalProductMetadata,
                getLocations as angular.IPromise<PlaceListShortModelPagingResult>,
                checkCanEditStickers as angular.IPromise<boolean>
            ]);
            this.model.main = response[0];
            this.model.linkTypes = response[1];
            this.model.main.genderItems = response[2];
            this.model.main.measurementTypes = response[3];
            this.productMetadata = response[4];
            this.stores = response[5].Items;
            const canEditStickers = response[6];
            this.canEditStickers = this.stores.length !== 0 && canEditStickers;
            if (this.state.isCopy) {
                this.model.main.title = this.model.main.title + this.$translate.instant('copy_space_beginning');
            }
            this.model.fillEditModel();
            this.model.loadedModel();

            this.$scope.$watch('vm.model.edit.itemType.ID', () => {
                this.onChangeItemType();
            });

            this.$scope.$watch(
                'vm.model.edit.genderItem.ID',
                async (n: number, o: number) => {
                    if (!this.model.edit) {
                        return;
                    }

                    if (!n) {
                        n = null;
                    }
                    await this.getMeasurements(n);
                    const categoryID = this.model.edit.category
                        ? this.model.edit.category.ID
                        : null;
                    if (categoryID) {
                        await this.getItemTypes(categoryID, n);
                    }
                    this.onChangeItemType();
                }
            );

            this.$scope.$watch(
                'vm.model.edit.extras',
                (newValue, oldValue) => {
                    if (newValue && newValue !== oldValue) {
                        if (oldValue) {
                            this.productForm.$setDirty();
                        }
                    }
                },
                true
            );

            this.$scope.$watch(
                'vm.model.edit.ingredientList',
                (newValue, oldValue) => {
                    if (newValue && newValue !== oldValue) {
                        if (oldValue) {
                            this.productForm.$setDirty();
                        }
                    }
                },
                true
            );

            this.$scope.$watch(
                'vm.variation',
                this.updateVariationPreview,
                true
            );

            this.profile = await this.accountService.getProfile();
            this.currencySign = this.profile.CurrencySign;
            this.currencyCssClass = this.getCurrencyCssClass(
                this.profile.CurrencySign
            );

            this.notificationService.clearAll();
        } catch (error) {
            this.notificationService.showErrorMessage(error);
        }
        this.waiting = false;
    }

    private productTypeCheck(categoryID: number) {
        if (categoryID === this.foodCategoryId || categoryID === this.groceryId) {
            if (categoryID === this.foodCategoryId) {
                this.IsFoodProduct = true;
                const country = this.model.edit.originCountry
                    ? this.originCountryList.find(
                        (c) => c.ID === this.model.edit.originCountry.ID
                    )
                    : undefined;
                if (country) {
                    this.model.edit.originCountry = country;
                }
            } else {
                this.IsFoodProduct = false;
            }
            this.isGroceryProduct = categoryID === this.groceryId;

            this.model.edit.allergens = _.map(
                this.allAllergens,
                (item): IFoodPreference => {
                    const exist = this.model.edit.allergens
                        ? this.model.edit.allergens.find(
                            (c) => c.ID === item.ID
                        )
                        : undefined;
                    return {
                        ID: item.ID,
                        Description: item.Description,
                        Name: item.Name,
                        IsSelected: exist ? exist.IsSelected : false
                    };
                }
            );

            this.model.edit.foodPreferences = _.map(
                this.allPreference,
                (item): IFoodPreference => {
                    const exist = this.model.edit.foodPreferences
                        ? this.model.edit.foodPreferences.find(
                            (c) => c.ID === item.ID
                        )
                        : undefined;
                    return {
                        ID: item.ID,
                        Description: item.Description,
                        Name: item.Name,
                        IsSelected: exist ? exist.IsSelected : false
                    };
                }
            );
        } else {
            this.IsFoodProduct = false;
            this.isGroceryProduct = false;
            this.model.edit.ingredientList = [];
            this.model.edit.variations.forEach((variation) => variation.ingredients = []);
        }
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/app.retailer/modules/products/productBase.controller.ts